import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import Dropdown from "../Dropdown";
import { formatSwedishTime } from "../../helpers/DateTime";
import { chartForecastActual, chartGradientDefaults } from "../../theme/gitoColors";

function toMs(dateLike) {
    if (!dateLike) return null;
    const d = new Date(String(dateLike));
    const t = d.getTime();
    return Number.isFinite(t) ? t : null;
}

function bucketStart(ts, granularity) {
    const d = new Date(ts);

    if (granularity === "Hour") {
        d.setMinutes(0, 0, 0);
        return d.getTime();
    }

    if (granularity === "Day") {
        d.setHours(0, 0, 0, 0);
        return d.getTime();
    }

    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

function formatX(val, granularity) {
    const dt = new Date(val);

    if (granularity === "Hour") {
        // Other charts show only HH:mm on hour-granularity.
        const s = formatSwedishTime(dt, false);
        return s && s.length >= 16 ? s.slice(11, 16) : s;
    }

    if (granularity === "Day") {
        return dt.toLocaleDateString("sv-SE");
    }

    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
}

function aggregateSeries(rows, granularity) {
    if (!Array.isArray(rows) || rows.length === 0) {
        return { forecast: [], actual: [] };
    }

    const forecastBuckets = new Map();
    const actualBuckets = new Map();

    for (const row of rows) {
        const x = toMs(row?.timeStamp);
        if (x == null) {
            continue;
        }

        const bucket = bucketStart(x, granularity);

        if (row.forecastKwh != null && Number.isFinite(Number(row.forecastKwh))) {
            const value = Number(row.forecastKwh);
            forecastBuckets.set(bucket, (forecastBuckets.get(bucket) || 0) + value);
        }

        if (row.actualKwh != null && Number.isFinite(Number(row.actualKwh))) {
            const value = Number(row.actualKwh);
            actualBuckets.set(bucket, (actualBuckets.get(bucket) || 0) + value);
        }
    }

    const timestamps = Array.from(
        new Set([...forecastBuckets.keys(), ...actualBuckets.keys()])
    ).sort((a, b) => a - b);

    const forecast = timestamps.map((x) => ({
        x,
        y: Number((forecastBuckets.get(x) || 0).toFixed(2)),
    }));

    const actual = timestamps.map((x) => ({
        x,
        y: Number((actualBuckets.get(x) || 0).toFixed(2)),
    }));

    return { forecast, actual };
}

export default function ForecastActualChart({ rows = [], title, infoText = "" }) {
    const { t } = useTranslation();
    const [granularity, setGranularity] = useState("Day");
    const chartTitle = title || t("forecast.chartTitle");

    const granularityOptions = [
        { value: "Hour", label: t("forecast.granularity.Hour") },
        { value: "Day", label: t("forecast.granularity.Day") },
        { value: "Month", label: t("forecast.granularity.Month") },
    ];

    const { forecast, actual } = useMemo(
        () => aggregateSeries(rows, granularity),
        [rows, granularity]
    );

    const { min, max } = useMemo(() => {
        const allPoints = [...forecast, ...actual];
        if (allPoints.length === 0) {
            const now = Date.now();
            return { min: now - 24 * 60 * 60 * 1000, max: now };
        }

        const xs = allPoints.map((p) => p.x);
        return { min: Math.min(...xs), max: Math.max(...xs) };
    }, [forecast, actual]);

    const options = useMemo(
        () => ({
            chart: {
                type: "area",
                height: 220,
                toolbar: { show: false },
                animations: { enabled: true, speed: 600, easing: "easeinout" },
            },
            stroke: {
                curve: "smooth",
                width: 3,
            },
            colors: chartForecastActual.colors,
            fill: {
                type: "gradient",
                gradient: {
                    ...chartGradientDefaults,
                    gradientToColors: chartForecastActual.gradientToColors,
                    opacityTo: 0.12,
                },
            },
            markers: { shape: "circle", size: 4, strokeWidth: 2, strokeColors: "#fff" },
            dataLabels: { enabled: false },
            legend: {
                position: "bottom",
                horizontalAlign: "center",
                onItemClick: { toggleDataSeries: false },
                onItemHover: { highlightDataSeries: true },
            },
            tooltip: {
                x: { formatter: (val) => formatX(val, granularity) },
                y: {
                    formatter: (v) =>
                        Number.isFinite(v) ? `${Number(v).toFixed(1)}` : "—",
                },
            },
            xaxis: {
                type: "datetime",
                datetimeUTC: false,
                min,
                max,
                labels: { formatter: (val) => formatX(val, granularity) },
            },
            yaxis: {
                labels: { formatter: (v) => Number(v).toFixed(0) },
            },
        }),
        [min, max, granularity]
    );

    const series = useMemo(
        () => [
            { name: t("forecast.seriesForecast"), data: forecast },
            { name: t("forecast.seriesActual"), data: actual },
        ],
        [forecast, actual, t]
    );

    const hasData = forecast.length > 0 || actual.length > 0;

    return (
        <div className="bg-white">
            <div className="flex items-start justify-between gap-3 mb-2">
                <h4 className="font-semibold text-sm text-slate-900 border-b border-slate-200 pb-1.5 flex-1">
                    {chartTitle}
                </h4>

                <div className="w-[7.5rem] shrink-0">
                    <Dropdown
                        options={granularityOptions}
                        selectedOptions={[granularity]}
                        onSelect={(v) => setGranularity(v)}
                    />
                </div>
            </div>

            {infoText ? (
                <p className="mb-3 text-xs text-mute-500">{infoText}</p>
            ) : null}

            {hasData ? (
                <Chart options={options} series={series} type="area" height={220} />
            ) : (
                <p className="text-sm text-mute-500 py-8 text-center">{t("forecast.noData")}</p>
            )}
        </div>
    );
}
